/**
 * 0.36.165 - Pagination element.
 *
 * Colors (Henaket palette, exact matches):
 *   --color-blue-800 (#355C8C)  active button bg + link/icon color
 *   --color-blue-600 (#8099B8)  active button border
 *   --color-blue-200 (#DDE5EE)  inactive button bg
 *   --color-blue-700 (#5A7AA2)  inactive button border
 *
 * Hover/pressed/focus follow the Henaket Button v1.2.2 progression:
 *   - Active button (Primary):    fill 800 -> 900 -> 1000, border 600 -> 700 -> 800
 *   - Inactive button (Secondary, blue text per pagination spec):
 *                                 fill 200 -> 300 -> 400, border 700 -> 800 -> 900
 *   - Focus ring: global *:focus-visible (magenta) via outline
 *
 * 0.36.165 changes:
 *   - Next link is HIDDEN (not disabled) on the last page (per request)
 *   - Page buttons use Henaket Button hover/active state progression
 *   - Optional "Show per page" dropdown using HenaketDropdown
 *     Values: 5 / 10 / 15 / 25 / 50 / 100. Default configurable.
 */
import { useState, useEffect } from 'react';
import HenaketDropdown from './HenaketDropdown';
import styles from './PagePagination.module.css';

const ChevronLeft = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <path d="M10 3 L 5 8 L 10 13" fill="none" stroke="currentColor"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <path d="M6 3 L 11 8 L 6 13" fill="none" stroke="currentColor"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronDoubleLeft = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <path d="M7 3 L 2 8 L 7 13 M 13 3 L 8 8 L 13 13" fill="none"
          stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronDoubleRight = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3 3 L 8 8 L 3 13 M 9 3 L 14 8 L 9 13" fill="none"
          stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PER_PAGE_OPTIONS = [5, 10, 15, 25, 50, 100];

function computePageList(active, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({ kind: 'page', value: i + 1 }));
  }
  if (active <= 4) {
    return [
      ...[1, 2, 3, 4, 5].map(n => ({ kind: 'page', value: n })),
      { kind: 'ellipsis' },
      { kind: 'page', value: total },
    ];
  }
  if (active >= total - 3) {
    return [
      { kind: 'page', value: 1 },
      { kind: 'ellipsis' },
      ...[total - 4, total - 3, total - 2, total - 1, total].map(n =>
        ({ kind: 'page', value: n })
      ),
    ];
  }
  return [
    { kind: 'page', value: 1 },
    { kind: 'ellipsis' },
    { kind: 'page', value: active - 1 },
    { kind: 'page', value: active },
    { kind: 'page', value: active + 1 },
    { kind: 'ellipsis' },
    { kind: 'page', value: total },
  ];
}


export default function PagePagination({
  totalPages: totalProp = 12,
  currentPage: currentProp = 1,
  pageLabel = 'Page',
  ofLabel = 'of',
  // 0.40.51 - Template for the page indicator, e.g.
  //   "Page {current_page} of {total_pages}"
  // Replaces the fixed `pageLabel + number + ofLabel + number`
  // order, which could not express languages that need the two
  // numbers in the other order, or a trailing word after the second
  // number. Empty/absent falls back to the old composition, so
  // existing pagination elements render unchanged.
  pageText = '',
  // 0.40.67 - Previous / Next button text. These were hardcoded
  // English strings, so an Armenian table showed "Previous"/"Next"
  // with no way to change them. Defaults keep existing behaviour for
  // any element that has not set them.
  prevLabel = 'Previous',
  nextLabel = 'Next',
  showPerPage = false,
  perPageDefault = 10,
  perPageLabel = 'Show per page',
  /* 0.45.86 - Own per-page choices, [{value, label}] (a Repeating
     display offers rows of cards: label 2, value 6). */
  perPageOptions = null,
  onChange,
  onPerPageChange,
  // 0.49.13 - Page buttons 32px instead of 40px.
  small = false,
}) {
  const total = Math.max(1, Number(totalProp) || 1);
  const initial = Math.min(Math.max(1, Number(currentProp) || 1), total);
  const [current, setCurrent] = useState(initial);

  // 0.45.86 - The allowed sizes: the caller's own choices when given.
  const allowedSizes = (Array.isArray(perPageOptions) && perPageOptions.length)
    ? perPageOptions.map(o => Number(o.value))
    : PER_PAGE_OPTIONS;
  const initialPerPage = allowedSizes.includes(Number(perPageDefault))
    ? Number(perPageDefault)
    : (Number(perPageDefault) > 0 ? Number(perPageDefault) : 10);
  const [perPage, setPerPage] = useState(initialPerPage);

  // 0.36.167 - keep internal state in sync with prop changes so the
  // component works when a parent drives currentPage from URL/state.
  // Without this, the parent's setParam({page:'3'}) would update the
  // prop but the internal `current` would stay where it was clicked.
  useEffect(() => {
    setCurrent(Math.min(Math.max(1, Number(currentProp) || 1), total));
  }, [currentProp, total]);
  useEffect(() => {
    const next = Number(perPageDefault) > 0 ? Number(perPageDefault) : 10;
    setPerPage(next);
  }, [perPageDefault]);

  const goTo = (p) => {
    const next = Math.min(Math.max(1, p), total);
    setCurrent(next);
    if (typeof onChange === 'function') onChange(next);
  };

  const items = computePageList(current, total);

  // 0.36.168 - Hide Previous when there's no previous page (current=1),
  // and hide Next when there's no next page (current=total). The earlier
  // "total > 7" guard on Previous was wrong: it left Previous visible on
  // page 1 of small lists (e.g. total=1, where Previous AND Next both
  // pointed nowhere). Page 1 means no previous, full stop.
  const showPrevious = current !== 1;
  const showNext = current !== total;

  return (
    <div className={small ? `${styles.wrapper} ${styles.small}` : styles.wrapper}>
      <nav className={styles.pagination} aria-label="Pagination">
        {/* Mobile single-line layout */}
        <div className={styles.mobile}>
          <button type="button" className={styles.mobileBtn}
                  onClick={() => goTo(1)} disabled={current === 1}
                  aria-label="First page">
            <ChevronDoubleLeft />
          </button>
          <button type="button" className={styles.mobileBtn}
                  onClick={() => goTo(current - 1)} disabled={current === 1}
                  aria-label={prevLabel}>
            <ChevronLeft />
          </button>
          <span className={styles.mobileText}>
            {(() => {
              const tpl = (pageText || '').trim();
              if (!tpl) return `${pageLabel} ${current} ${ofLabel} ${total}`;
              // Substituted, not string-formatted: the template is
              // admin-authored and may place the tokens in any order,
              // repeat them, or omit one.
              return tpl
                .split('{current_page}').join(String(current))
                .split('{total_pages}').join(String(total));
            })()}
          </span>
          <button type="button" className={styles.mobileBtn}
                  onClick={() => goTo(current + 1)} disabled={current === total}
                  aria-label={nextLabel}>
            <ChevronRight />
          </button>
          <button type="button" className={styles.mobileBtn}
                  onClick={() => goTo(total)} disabled={current === total}
                  aria-label="Last page">
            <ChevronDoubleRight />
          </button>
        </div>

        {/* Desktop layout */}
        <div className={styles.desktop}>
          <div className={styles.previousSlot}>
            {showPrevious && (
              <button type="button" className={styles.navLink}
                      onClick={() => goTo(current - 1)}
                      aria-label="Previous page">
                <ChevronLeft />
                <span>{prevLabel}</span>
              </button>
            )}
          </div>

          <ol className={styles.pagesCluster}>
            {items.map((it, idx) => (
              <li key={idx} className={styles.pageItem}>
                {it.kind === 'ellipsis' ? (
                  <span className={styles.ellipsis} aria-hidden="true">...</span>
                ) : (
                  <button type="button"
                          className={`${styles.pageBtn} ${it.value === current ? styles.pageBtnActive : ''}`}
                          onClick={() => goTo(it.value)}
                          aria-current={it.value === current ? 'page' : undefined}
                          aria-label={`Page ${it.value}`}>
                    {it.value}
                  </button>
                )}
              </li>
            ))}
          </ol>

          <div className={styles.nextSlot}>
            {showNext && (
              <button type="button" className={styles.navLink}
                      onClick={() => goTo(current + 1)}
                      aria-label="Next page">
                <span>{nextLabel}</span>
                <ChevronRight />
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Optional "Show per page" cluster — hidden when showPerPage=false. */}
      {showPerPage && (
        <div className={styles.perPageCluster}>
          <label className={styles.perPageLabel} htmlFor="pagination-per-page">
            {perPageLabel}
          </label>
          <div className={styles.perPageSelect}>
            <HenaketDropdown
              id="pagination-per-page"
              value={String(perPage)}
              options={(Array.isArray(perPageOptions) && perPageOptions.length
                ? perPageOptions
                : PER_PAGE_OPTIONS.map(n => ({ value: String(n), label: String(n) })))}
              // 0.36.690 — Force the dropdown UI regardless of option
              // count. HenaketDropdown's default behavior auto-expands
              // to a radio group when options.length <= platform's
              // inline threshold (7 by default). The per-page selector
              // has 6 options today (5/10/15/25/50/100), which used
              // to render as a horizontal radio strip — visually
              // clashes with the numbered page buttons above it, and
              // takes horizontal space that the paginator doesn't
              // budget for. Setting `inlineThreshold={0}` disables
              // the auto-expansion for THIS dropdown only; the rest
              // of the platform's dropdowns keep the admin-configured
              // default.
              inlineThreshold={0}
              onChange={(v) => {
                const next = parseInt(v, 10);
                setPerPage(next);
                if (typeof onPerPageChange === 'function') onPerPageChange(next);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
